import {format, validation} from "../i18n";

const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const date = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });
const calendarDate = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' });

export const formatDateTime = (value?: string) => value ? dateTime.format(new Date(value)) : '';
export const formatDate = (value?: string) => value ? date.format(new Date(value)) : '';

// For dates that were picked as a day, not a moment, such as a version's releasedOn: they're stored as midnight UTC on
// that day, so they're shown in UTC. In local time, that's the day before anywhere west of Greenwich.
export const formatCalendarDate = (value?: string) => value ? calendarDate.format(new Date(value)) : '';

// The value for a date input: a calendar date's day (see formatCalendarDate), or today where the user is.
export function toDateInput(value?: string): string {
    if (value) {
        return new Date(value).toISOString().substring(0, 10);
    }
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// A date input's value as a calendar date: midnight UTC on that day.
export const fromDateInput = (value: string) => `${value}T00:00:00.000Z`;

// Same rules as the API (SharedUtils.IsValidEmailAddress / IsPlusAddressed), so the form catches what it would refuse.
// The API's rule (tristar-corp-api SharedUtils.IsValidUsername).
export function usernameProblem(username: string): string | undefined {
    const value = username.trim();
    if (!value) {
        return validation.usernameRequired;
    }
    if (!/^[A-Za-z0-9_-]+$/.test(value)) {
        return validation.usernameCharacters;
    }
    return undefined;
}

// plusAllowed: for the mailing list, where people use plus-addressing to sort what they've signed up for (accounts
// refuse it).
export function emailProblem(email: string, options: { plusAllowed?: boolean } = {}): string | undefined {
    const value = email.trim();
    if (!/^[^\s@<>()[\]\\.,;:"]+(\.[^\s@<>()[\]\\.,;:"]+)*@([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}$/.test(value)) {
        return validation.emailInvalid;
    }
    if (!options.plusAllowed && value.split('@')[0].includes('+')) {
        return validation.emailPlusAddressed;
    }
    return undefined;
}

export const MIN_PASSWORD_LENGTH = 10;

export function newPasswordProblem(password: string, confirm: string): string | undefined {
    if (password.length < MIN_PASSWORD_LENGTH) {
        return format(validation.passwordTooShort, { min: MIN_PASSWORD_LENGTH });
    }
    if (password !== confirm) {
        return validation.passwordsDontMatch;
    }
    return undefined;
}

// amount is in the currency's minor units, however many the currency has (2 for USD, 0 for JPY).
export function formatPrice(amount: number, currency: string): string {
    try {
        const format = new Intl.NumberFormat(undefined, { style: 'currency', currency });
        return format.format(amount / 10 ** (format.resolvedOptions().maximumFractionDigits ?? 2));
    } catch {
        return `${amount} ${currency}`;
    }
}

// The inverse of formatPrice's scaling: "49.99" USD → 4999. Undefined when it isn't a non-negative number.
export function toMinorUnits(text: string, currency: string): number | undefined {
    const value = Number(text.trim());
    if (!text.trim() || !isFinite(value) || value < 0) {
        return undefined;
    }
    let digits = 2;
    try {
        digits = new Intl.NumberFormat(undefined, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
    } catch {
        // Unknown currency: the API refuses it anyway.
    }
    return Math.round(value * 10 ** digits);
}

export function fromMinorUnits(amount: number, currency: string): string {
    let digits = 2;
    try {
        digits = new Intl.NumberFormat(undefined, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
    } catch {
        // Keep 2.
    }
    return (amount / 10 ** digits).toFixed(digits);
}

export function formatBytes(bytes?: number): string {
    if (bytes === undefined) {
        return '';
    }
    const units = ['bytes', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit++;
    }
    return unit === 0 ? `${value} ${units[0]}` : `${value.toFixed(1)} ${units[unit]}`;
}

// Same rules as the API (ProductRouteResults.IsValidVersion).
export const isValidVersion = (version: string) => /^v?\d+(\.\d+){0,3}(-[0-9A-Za-z.-]+)?$/.test(version.trim());
